import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';

interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminUserManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [currentProfile, setCurrentProfile] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'profile'>('users');

  // Form state for creating user
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'evaluation_admin',
  });

  // Form state for profile
  const [profileData, setProfileData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  };

  // Load admin users
  const loadAdminUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/users?userId=${user?.id}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setAdminUsers(data.data || []);
      } else {
        setError(data.message || 'Failed to load admin users');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  // Load current profile
  const loadProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/profile?userId=${user?.id}`,
        { headers: getAuthHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        setCurrentProfile(data.data);
        setProfileData({
          email: data.data.email || '',
          firstName: data.data.firstName || '',
          lastName: data.data.lastName || '',
          password: '',
          confirmPassword: '',
        });
      } else {
        setError(data.message || 'Failed to load profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // Create admin user
  const handleCreateUser = async () => {
    if (!formData.email || !formData.password || !formData.role) {
      setError('Email, password, and role are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/users?userId=${user?.id}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName || null,
            lastName: formData.lastName || null,
            role: formData.role,
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Admin user created successfully');
        setDialogOpen(false);
        setFormData({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          role: 'evaluation_admin',
        });
        loadAdminUsers();
      } else {
        setError(data.message || 'Failed to create admin user');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create admin user');
    } finally {
      setLoading(false);
    }
  };

  // Update profile
  const handleUpdateProfile = async () => {
    if (profileData.password && profileData.password !== profileData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/admin/profile?userId=${user?.id}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            email: profileData.email,
            firstName: profileData.firstName || null,
            lastName: profileData.lastName || null,
            password: profileData.password || undefined,
          }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Profile updated successfully');
        setProfileDialogOpen(false);
        setProfileData({
          ...profileData,
          password: '',
          confirmPassword: '',
        });
        loadProfile();
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      loadAdminUsers();
    } else {
      loadProfile();
    }
  }, [activeTab]);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'error';
      case 'admin':
        return 'primary';
      case 'evaluation_admin':
        return 'warning';
      case 'contest_admin':
        return 'success';
      default:
        return 'default';
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Admin User Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="My Profile" value="profile" icon={<PersonIcon />} iconPosition="start" />
        <Tab label="Admin Users" value="users" icon={<PersonIcon />} iconPosition="start" />
      </Tabs>

      {activeTab === 'profile' && (
        <Card>
          <CardContent>
            {currentProfile ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Profile Information</Typography>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => setProfileDialogOpen(true)}
                  >
                    Edit Profile
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {currentProfile.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Role
                    </Typography>
                    <Chip
                      label={currentProfile.role}
                      color={getRoleColor(currentProfile.role)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      First Name
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {currentProfile.firstName || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {currentProfile.lastName || 'Not set'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {new Date(currentProfile.createdAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Alert severity="info">Loading profile...</Alert>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'users' && (
        <Box>
          {isSuperAdmin && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Create Admin User
              </Button>
            </Box>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && adminUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : adminUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No admin users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  adminUsers.map((adminUser) => (
                    <TableRow key={adminUser.id} hover>
                      <TableCell>{adminUser.email}</TableCell>
                      <TableCell>
                        {adminUser.firstName || adminUser.lastName
                          ? `${adminUser.firstName || ''} ${adminUser.lastName || ''}`.trim()
                          : 'Not set'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={adminUser.role}
                          color={getRoleColor(adminUser.role)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={adminUser.isActive ? 'Active' : 'Inactive'}
                          color={adminUser.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(adminUser.createdAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Create User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Admin User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="super_admin">Super Admin</MenuItem>
                <MenuItem value="evaluation_admin">Evaluation Admin</MenuItem>
                <MenuItem value="contest_admin">Contest Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            />
            <TextField
              label="First Name"
              fullWidth
              value={profileData.firstName}
              onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={profileData.lastName}
              onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
            />
            <TextField
              label="New Password (leave blank to keep current)"
              type="password"
              fullWidth
              value={profileData.password}
              onChange={(e) => setProfileData({ ...profileData, password: e.target.value })}
            />
            {profileData.password && (
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                value={profileData.confirmPassword}
                onChange={(e) => setProfileData({ ...profileData, confirmPassword: e.target.value })}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateProfile}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserManagement;


